import React, { useState, useEffect } from 'react';
import './Testimonials.css';

function Testimonials() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [direction, setDirection] = useState('next');

  const testimonials = [
    {
      quote: "Working with STOREZI was a game-changer for our business. They transformed our outdated store into a modern, high-converting platform that our customers love. Sales increased by 45% in just three months.",
      author: "Sarah Johnson",
      role: "Founder & CEO",
      company: "Luxe Fashion",
      avatar: "SJ",
      rating: 5
    },
    {
      quote: "The attention to detail and understanding of e-commerce best practices is exceptional. STOREZI didn't just design a beautiful store—they created an experience that drives real business results.",
      author: "Michael Chen",
      role: "Chief Digital Officer",
      company: "TechGear Pro",
      avatar: "MC",
      rating: 5
    },
    {
      quote: "From day one, the team at STOREZI understood our vision and brought it to life beyond our expectations. The new store has elevated our brand and significantly improved customer engagement.",
      author: "Emma Williams",
      role: "Owner",
      company: "Organic Beauty Co.",
      avatar: "EW",
      rating: 5
    },
    {
      quote: "The ROI speaks for itself. Within 6 months of launching our new store, we saw a 250% return on investment. STOREZI's strategic approach to design and conversion optimization truly works.",
      author: "David Rodriguez",
      role: "Marketing Director",
      company: "FitLife Nutrition",
      avatar: "DR",
      rating: 5
    }
  ];

  const nextTestimonial = () => {
    if (isTransitioning) return;
    setDirection('next');
    setIsTransitioning(true);
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
    setTimeout(() => setIsTransitioning(false), 600);
  };

  const prevTestimonial = () => {
    if (isTransitioning) return;
    setDirection('prev');
    setIsTransitioning(true);
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setTimeout(() => setIsTransitioning(false), 600);
  };

  const goToTestimonial = (index) => {
    if (isTransitioning || index === activeIndex) return;
    setDirection(index > activeIndex ? 'next' : 'prev');
    setIsTransitioning(true);
    setActiveIndex(index);
    setTimeout(() => setIsTransitioning(false), 600);
  };

  // Auto-play carousel
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isTransitioning) {
        nextTestimonial();
      }
    }, 6000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTransitioning]);

  return (
    <section id="testimonials" className="testimonials-section">
      <div className="container">
        <div className="section-header fade-in">
          <div className="section-badge">Client Stories</div>
          <h2 className="section-title">
            Trusted by
            <span className="gradient-text"> Growing Businesses</span>
          </h2>
          <p className="section-description">
            Don't just take our word for it. Hear from the businesses we've helped grow.
          </p>
        </div>

        <div className="testimonials-carousel fade-in">
          <div className="carousel-background">
            <div className="carousel-glow"></div>
            <div className="floating-quotes">
              <span className="quote-mark quote-left">"</span>
              <span className="quote-mark quote-right">"</span>
            </div>
          </div>

          <div className="carousel-container">
            <div
              className={`carousel-track ${isTransitioning ? `transitioning-${direction}` : ''}`}
              style={{ transform: `translateX(-${activeIndex * 100}%)` }}
            >
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className={`testimonial-slide ${index === activeIndex ? 'active' : ''}`}
                >
                  <div className="testimonial-card">
                    <div className="card-shimmer"></div>

                    <div className="testimonial-rating">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <svg
                          key={i}
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="star"
                          style={{ animationDelay: `${i * 0.1}s` }}
                        >
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                        </svg>
                      ))}
                    </div>

                    <div className="quote-icon-top">
                      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                        <path d="M12 24C12 20.6863 14.6863 18 18 18V18C18 14.6863 15.3137 12 12 12V12C8.68629 12 6 14.6863 6 18V24C6 27.3137 8.68629 30 12 30V30V24Z" fill="currentColor" opacity="0.15"/>
                        <path d="M36 24C36 20.6863 38.6863 18 42 18V18C42 14.6863 39.3137 12 36 12V12C32.6863 12 30 14.6863 30 18V24C30 27.3137 32.6863 30 36 30V30V24Z" fill="currentColor" opacity="0.15"/>
                      </svg>
                    </div>

                    <p className="testimonial-quote">
                      <span className="quote-text">{testimonial.quote}</span>
                    </p>

                    <div className="testimonial-author">
                      <div className="author-avatar-wrapper">
                        <div className="avatar-ring"></div>
                        <div className="author-avatar">{testimonial.avatar}</div>
                      </div>
                      <div className="author-info">
                        <div className="author-name">{testimonial.author}</div>
                        <div className="author-role">{testimonial.role}</div>
                        <div className="author-company">{testimonial.company}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="carousel-controls">
            <button
              className={`carousel-btn prev ${isTransitioning ? 'disabled' : ''}`}
              onClick={prevTestimonial}
              disabled={isTransitioning}
              aria-label="Previous testimonial"
            >
              <span className="btn-bg"></span>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            <div className="carousel-indicators">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${index === activeIndex ? 'active' : ''}`}
                  onClick={() => goToTestimonial(index)}
                  disabled={isTransitioning}
                  aria-label={`Go to testimonial ${index + 1}`}
                >
                  <span className="indicator-fill"></span>
                </button>
              ))}
            </div>

            <button
              className={`carousel-btn next ${isTransitioning ? 'disabled' : ''}`}
              onClick={nextTestimonial}
              disabled={isTransitioning}
              aria-label="Next testimonial"
            >
              <span className="btn-bg"></span>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="social-proof fade-in">
          <div className="proof-item">
            <div className="proof-icon-wrapper">
              <div className="proof-icon">⭐</div>
              <div className="icon-glow"></div>
            </div>
            <div className="proof-text">
              <strong>5.0 Average Rating</strong>
              <span>Based on 50+ reviews</span>
            </div>
          </div>
          <div className="proof-item">
            <div className="proof-icon-wrapper">
              <div className="proof-icon">🏆</div>
              <div className="icon-glow"></div>
            </div>
            <div className="proof-text">
              <strong>Shopify Partner</strong>
              <span>Certified & trusted</span>
            </div>
          </div>
          <div className="proof-item">
            <div className="proof-icon-wrapper">
              <div className="proof-icon">💯</div>
              <div className="icon-glow"></div>
            </div>
            <div className="proof-text">
              <strong>99% Client Satisfaction</strong>
              <span>Happy clients every time</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Testimonials;
